/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ApiRequestApprovedInterface,
  RequestApprovedApiResponseInterface,
  RequestApprovedInitialProperties,
} from '../../interface/request-approved-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import penaltyManagementApi from '../../utils/api/penalty-management';

export const fetchRequestApprovedAccount = createAsyncThunk<
  RequestApprovedApiResponseInterface, // Return type
  ApiRequestApprovedInterface
>(
  'request-approved-account/fetchRequestApprovedAccount',
  async (data: ApiRequestApprovedInterface, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestAccountByStatus(
        removeEmptyKeys({ ...data, requestStatus: 'approved' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchRequestApprovedAccountFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'request-approved-account/fetchApprovedAccountFilters',
  async (_, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestAccountFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: RequestApprovedInitialProperties = {
  requestApproved: [],
  limit: 0,
  page: 0,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const requestApprovedAccountSlice = createSlice({
  name: 'request-approved-account',
  initialState,
  reducers: {
    /**
     * Update the approved filters
     * @param {any} state - The current state of the request approved account slice
     * @param {any} action - The payload action containing the filter update properties
     */
    updateApprovedAccountFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state of the request approved account slice
     */
    disableApprovedAccountFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function to change sortBy
     * @param {any} state - The current state of the request approved account slice
     * @param {any} action - The payload action containing the sort field and sort order
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Function to handle async actions
   * @param {any} builder - The action reducer map builder for the request approved account slice
   */
  extraReducers: (builder: ActionReducerMapBuilder<RequestApprovedInitialProperties>) => {
    builder
      .addCase(fetchRequestApprovedAccount.pending, (state: RequestApprovedInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchRequestApprovedAccount.fulfilled,
        (
          state: RequestApprovedInitialProperties,
          action: PayloadAction<RequestApprovedApiResponseInterface>,
        ) => {
          state.loading = false;
          state.requestApproved = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchRequestApprovedAccount.rejected, (state: RequestApprovedInitialProperties) => {
        state.loading = false;
      });
    builder
      .addCase(
        fetchRequestApprovedAccountFilters.pending,
        (state: RequestApprovedInitialProperties) => {
          state.filterListLoading = true;
        },
      )
      .addCase(
        fetchRequestApprovedAccountFilters.fulfilled,
        (
          state: RequestApprovedInitialProperties,
          action: PayloadAction<CommonFilterProperties[]>,
        ) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...(action.payload || [])];
        },
      )
      .addCase(
        fetchRequestApprovedAccountFilters.rejected,
        (state: RequestApprovedInitialProperties) => {
          state.filterListLoading = false;
        },
      );
  },
});
export const { updateApprovedAccountFilters, disableApprovedAccountFilterApplied, setSortData } =
  requestApprovedAccountSlice.actions;
export default requestApprovedAccountSlice.reducer;
